import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';

const KEEP_ALIVE_INTERVAL = 20 * 60 * 1000;
const IDLE_TIMEOUT = 45 * 60 * 1000;
const AVISO_ANTES = 60 * 1000;

export default function SessionKeepAlive() {
  const { user, logout } = useAuth();
  const [warningOpen, setWarningOpen] = useState(false);
  const [countdown, setCountdown] = useState(60);

  const timersRef = useRef({
    keepAlive: null,
    warningTrigger: null,
    logoutAction: null,
    countdownInterval: null,
  });

  const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  const isLogged = Boolean(user);

  const limparTimers = useCallback(() => {
    const t = timersRef.current;
    if (t.warningTrigger) { clearTimeout(t.warningTrigger); t.warningTrigger = null; }
    if (t.logoutAction) { clearTimeout(t.logoutAction); t.logoutAction = null; }
    if (t.countdownInterval) { clearInterval(t.countdownInterval); t.countdownInterval = null; }
  }, []);

  const iniciarAviso = useCallback(() => {
    setWarningOpen(true);

    let segundos = 60;
    setCountdown(segundos);

    timersRef.current.logoutAction = setTimeout(() => {
      setWarningOpen(false);
      logout();
    }, AVISO_ANTES);

    timersRef.current.countdownInterval = setInterval(() => {
      segundos -= 1;
      if (segundos <= 0) {
        clearInterval(timersRef.current.countdownInterval);
        timersRef.current.countdownInterval = null;
      }
      setCountdown(segundos);
    }, 1000);
  }, [logout]);

  const resetarInatividade = useCallback(() => {
    limparTimers();
    setWarningOpen(false);
    setCountdown(60);

    timersRef.current.warningTrigger = setTimeout(() => {
      iniciarAviso();
    }, IDLE_TIMEOUT - AVISO_ANTES);
  }, [limparTimers, iniciarAviso]);

  const handleContinuar = useCallback(async () => {
    resetarInatividade();

    const currentToken = localStorage.getItem('token');
    if (!currentToken) return;
    try {
      const response = await axios.post(`${baseURL}/auth/refresh`, {}, {
        headers: { Authorization: `Bearer ${currentToken}` },
      });
      localStorage.setItem('token', response.data.token);
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
        window.dispatchEvent(new Event('auth:user-updated'));
      }
    } catch (err) {
      console.error('Erro ao renovar token (manual):', err.message);
    }
  }, [baseURL, resetarInatividade]);

  useEffect(() => {
    if (!isLogged) return;

    const renovarToken = async () => {
      const currentToken = localStorage.getItem('token');
      if (!currentToken) return;
      try {
        const response = await axios.post(`${baseURL}/auth/refresh`, {}, {
          headers: { Authorization: `Bearer ${currentToken}` },
        });
        localStorage.setItem('token', response.data.token);
        if (response.data.user) {
          localStorage.setItem('user', JSON.stringify(response.data.user));
          window.dispatchEvent(new Event('auth:user-updated'));
        }
      } catch (err) {
        console.error('Erro ao renovar token (keep-alive):', err.message);
      }
    };

    renovarToken();

    timersRef.current.keepAlive = setInterval(renovarToken, KEEP_ALIVE_INTERVAL);

    return () => {
      if (timersRef.current.keepAlive) clearInterval(timersRef.current.keepAlive);
    };
  }, [isLogged, baseURL]);

  useEffect(() => {
    if (!isLogged) return;

    const eventos = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    const aoDetectarAtividade = () => {
      resetarInatividade();
    };

    resetarInatividade();

    eventos.forEach((evt) => {
      window.addEventListener(evt, aoDetectarAtividade, { passive: true });
    });

    return () => {
      eventos.forEach((evt) => {
        window.removeEventListener(evt, aoDetectarAtividade);
      });
      limparTimers();
    };
  }, [isLogged, resetarInatividade, limparTimers]);

  if (!user) return null;

  return (
    <Dialog
      open={warningOpen}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return;
      }}
      aria-labelledby="session-warning-title"
    >
      <DialogTitle id="session-warning-title">
        Sessao prestes a expirar
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Sua sessao sera encerrada por inatividade em {countdown} segundos.
          Deseja continuar utilizando o sistema?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={logout} color="error">
          Sair
        </Button>
        <Button onClick={handleContinuar} variant="contained" autoFocus>
          Continuar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
